import React, { useState, useCallback, useRef, useEffect } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "../../config.js";

const MapPicker = ({ onLocationSelect }) => {
  const mapRef = useRef();
  const [selected, setSelected] = useState(null);
  const [center, setCenter] = useState({ lat: -17.8292, lng: 31.0522 }); // Default: Harare coordinates
  const [userLocation, setUserLocation] = useState(null);

  // Get user's current location on component mount
  useEffect(() => {
    console.log('🔍 Starting geolocation request...');
    
    if (navigator.geolocation) {
      console.log('✅ Geolocation supported by browser');
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userCoords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          console.log('📍 Raw position data:', position);
          console.log('📍 Coordinates:', userCoords);
          console.log('📍 Accuracy:', position.coords.accuracy, 'meters');
          console.log('📍 Timestamp:', new Date(position.timestamp));
          
          setCenter(userCoords);
          setUserLocation(userCoords);
          console.log('✅ User location set:', userCoords);
        },
        (error) => {
          console.error('❌ Geolocation error:', error);
          console.error('❌ Error code:', error.code);
          console.error('❌ Error message:', error.message);
          
          // Keep default center (Harare) if geolocation fails
          console.log('🔄 Falling back to default location (Harare)');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 180000 // 3 minutes = 180,000 milliseconds
        }
      );
    } else {
      console.warn('❌ Geolocation is not supported by this browser');
      console.log('🔄 Falling back to default location (Harare)');
    }
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-maps-script',
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries: ["places"],
  });

  const handleMapClick = (event) => {
    setSelected({ lat: event.latLng.lat(), lng: event.latLng.lng() });
  };

  const handleConfirmLocation = () => {
    if (selected) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: selected }, (results, status) => {
        if (status === "OK" && results[0]) {
          onLocationSelect(selected, results[0].formatted_address);
        }
      });
    }
  };

  if (loadError) return <div className="p-4 text-red-500">Error loading maps</div>;
  if (!isLoaded) return <div className="p-4">Loading maps...</div>;

  return (
    <div className="relative w-screen ">
      <GoogleMap
        ref={mapRef}
        mapContainerStyle={{ height: "90vh", width: "100vw" }}
        zoom={10} // Increased from 10 to 15 for better detail
        center={center}
        onClick={handleMapClick}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
      >
        {/* User's current location marker */}
        {userLocation && (
          <Marker 
            position={userLocation} 
            title="Your Current Location"
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12, // Increased size for better visibility
              fillColor: "#9B59B6", // Purple color for user location
              fillOpacity: 0.8,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
            }}
            zIndex={1000} // Ensure user location marker is on top
          />
        )}
        {/* Debug: Show if user location is available */}
        {!userLocation && (
          <div style={{
            position: 'absolute',
            top: 10,
            left: 10,
            background: 'rgba(255,255,255,0.9)',
            padding: '8px',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#666',
            zIndex: 1000
          }}>
            Getting your location...
          </div>
        )}
        {/* Selected location marker */}
        {selected && <Marker position={selected} />}
      </GoogleMap>
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
        <button
          onClick={handleConfirmLocation}
          disabled={!selected}
          className="w-full bg-[#ffc000] hover:bg-[#e3ab00] text-gray-900 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Confirm Location
        </button>
      </div>
    </div>
  );
};

export default MapPicker;